import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('recovery_ai.brand_voice')

// Shared presentational textarea for the brand-voice surfaces (the Settings pane
// and the Agent-Tuning modal). W7 (FR-6) collapsed the old three-radio "sources"
// to this one controlled field; the grey read-only default preview and the
// orphaned wizard step that also lived here are gone.

// Skeleton bars shown inside the box while a "Generate from documents"
// derivation is in flight (state 4). Named per CLAUDE.md "extract magic numbers".
export const SKELETON_BAR_HEIGHT = '12px'
export const SKELETON_BAR_WIDTH_LONG = '100%'
export const SKELETON_BAR_WIDTH_SHORT = '30%'

export const skeletonBarStyle = (width: string) => ({
  height: SKELETON_BAR_HEIGHT,
  width,
  borderRadius: '4px',
  backgroundColor: merchantTheme.colors.grey10,
})

type TextareaProps = {
  value: string
  onChange: (value: string) => void
  // True while a "Generate from documents" derivation is in flight: the field
  // goes readOnly + aria-busy and its content is covered by skeleton bars.
  // readOnly (not disabled) keeps it in the tab order and exposed to AT.
  isDeriving?: boolean
  // AI-64 CASL gate: the host has `access` but not `manage` on RecoveryAI. The
  // saved voice still renders (this is the read path), but the field is
  // disabled — mirroring the disabled FormControl inputs the sibling
  // EscalationRules / DailyBudget panes use for the same ability.
  disabled?: boolean
}

export const BrandVoiceTextarea = ({
  value,
  onChange,
  isDeriving = false,
  disabled = false,
}: TextareaProps) => {
  const [css] = useStyletron()

  return (
    <div className={css({ marginTop: '16px', position: 'relative' })}>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        readOnly={isDeriving}
        disabled={disabled}
        aria-busy={isDeriving}
        placeholder={t('write_placeholder')}
        className={css({
          width: '100%',
          minHeight: '160px',
          padding: '16px',
          border: `1px solid ${merchantTheme.colors.grey40}`,
          borderRadius: '4px',
          fontSize: '16px',
          lineHeight: '24px',
          // A disabled textarea gets a UA-grey text colour that fails contrast
          // on the long paragraph a view-only operator is here to READ, so pin
          // the same grey90 in both states; `disabled` still conveys the state.
          color: merchantTheme.colors.grey90,
          backgroundColor: disabled ? merchantTheme.colors.grey05 : 'white',
          resize: 'vertical',
          fontFamily: 'Lato, sans-serif',
        })}
        aria-label={t('textarea_aria_label')}
      />
      {isDeriving && (
        <div
          // Decorative overlay — aria-busy on the textarea already conveys the
          // "content is arriving" state to assistive tech.
          aria-hidden="true"
          data-testid="brand-voice-skeleton"
          className={css({
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '16px',
            borderRadius: '4px',
            backgroundColor: 'white',
          })}
        >
          <div className={css(skeletonBarStyle(SKELETON_BAR_WIDTH_LONG))} />
          <div className={css(skeletonBarStyle(SKELETON_BAR_WIDTH_SHORT))} />
        </div>
      )}
    </div>
  )
}
